import React, { ReactNode, useEffect } from "react";
import { ErrorBoundary, useRollbar } from "@rollbar/react";
import { useRouteError } from "react-router-dom";
import { LocalisedErrorPage } from "./ErrorPage.component.jsx";
import { useTranslation } from "components/translations/useTranslation";

// a full load of the app root - reloading the current url would just re-run whatever crashed
const goHome = () => window.location.assign("/");

// only reached when the localised page below failed to render too, so it deliberately leans on
// nothing - no store, no router, no dictionaries, not even a stylesheet
export const LastResortFallback = (): React.ReactElement => (
    <div style={{ maxWidth: "600px", margin: "80px auto", padding: "0 24px", fontSize: "14px", lineHeight: "21px" }}>
        <div style={{ fontSize: "20px", fontWeight: 700, marginBottom: "16px" }}>Something went wrong</div>
        <p style={{ marginBottom: "24px" }}>
            Approval ran into an unexpected error and cannot show this page. The error has been
            logged. Please go back to the start page, and contact support if the problem persists.
        </p>
        <button type="button" className="btn btn-primary" onClick={goHome}>Go to the start page</button>
    </div>
);

/**
 * Outermost boundary, above the redux provider. Without a fallbackUI @rollbar/react renders null,
 * which is what turned any render error into a blank page (AC-12687).
 */
export const LastResortBoundary = ({ children }: { children: ReactNode }): React.ReactElement => (
    <ErrorBoundary fallbackUI={LastResortFallback} errorMessage="app crash escaped the localised error page">
        {children}
    </ErrorBoundary>
);

/**
 * The serverError page, but its action leaves the crashing url behind - the plain `ServerError`
 * offers a refresh, which for a render crash just runs the same broken render again.
 */
export const AppCrashFallback = (): React.ReactElement => {
    const { translate } = useTranslation();

    return (
        <LocalisedErrorPage
            errorType="serverError"
            translate={translate}
            action={
                <button type="button" className="btn btn-primary" onClick={goHome}>
                    {translate("errorPage.goToHomepage")}
                </button>
            }
        />
    );
};

/** Inside the redux provider, so the crash page can be shown in the user's own language. */
export const AppErrorBoundary = ({ children }: { children: ReactNode }): React.ReactElement => (
    <ErrorBoundary fallbackUI={AppCrashFallback} errorMessage="app crashed">
        {children}
    </ErrorBoundary>
);

/**
 * React Router catches a crash inside a route itself, so AppErrorBoundary never sees it and the
 * user got react-router's own "Unexpected Application Error" screen (AC-12313). Wired as the root
 * route's errorElement, this shows the same page as AppErrorBoundary and reports as it would.
 */
export const RouteCrashFallback = (): React.ReactElement => {
    const error = useRouteError();
    const rollbar = useRollbar();

    useEffect(() => {
        // useRouteError also hands back Responses and other thrown values, which Rollbar cannot read as a stack
        if (error instanceof Error) {
            rollbar.error("route crashed", error);
        } else {
            rollbar.error("route crashed with a non-error value", { routeError: error });
        }
    }, [error, rollbar]);

    return <AppCrashFallback/>;
};
